import {ResizeObserver} from "@juggle/resize-observer";
import cloneDeep from "@/assets/plugins/mps-common/expansions/clone/cloneDeep";

const getUniqueId = () => Math.random().toString(36).substr(2, 9);

/**
 * Th 의 실질적인 DOM Element 를 래핑하여 관리하는 클래스입니다.
 *
 * 여기에는 다음과 같은 기능이 포함됩니다.
 *  - ResizeObserver (각 헤더 DOM Element 의 width, height 를 실시간으로 관리합니다.)
 *  - 부모 헤더의 clientHeight 를 관리
 */
class ThElement {
    _node;
    _element;
    _width;
    _height;

    constructor(node) {
        this._node = node;
    }

    /**
     * th 의 table id attribute 로 DOM Element 를 찾습니다.
     *
     * - QuerySelector('th[th-id="id"]')
     */
    findElementByTableId() {
        if (!this._node.id) return;
        this._element = document.querySelector("th[th-id='" + this._node.id + "']");
    }

    /**
     * th 엘리먼트에 상태를 감시하는 Observer 를 등록합니다.
     *
     * 등록되는 Observer
     *  - ResizeObserver
     */
    observe() {
        if (!this._element) return;
        const resizeObserver = new ResizeObserver(entries => {
            entries.forEach(entry => {
                const { inlineSize, blockSize } = entry.borderBoxSize[0];
                this._width = inlineSize
                this._height = blockSize
            })
        });

        resizeObserver.observe(this._element);
    }

    get width() {
        return this._width || 0;
    }

    get height() {
        return this._height || 0;
    }

    /**
     * 모든 부모 Dom Element th 의 height 를 합한 값을 반환합니다.
     */
    get parentHeight() {
        let parent = this._node.parent;
        let total = 0;
        while (!!parent) {
            total += parent._elementObject.height;
            parent = parent.parent; // 주의하세요! 이 로직이 빠지면 무한루프에 빠집니다.
        }
        return total;
    }
}

/**
 * 헤더 노드입니다.
 *
 * 각 노드는 다음을 가지고 있습니다.
 *      - 헤더의 원본 데이터
 *      - 헤더의 부모 노드
 *      - 헤더의 자식 노드들
 *      - 고유 id
 *      - 본인의 rowspan
 *      - 본인의 colspan
 */
class ThNode {
    /** 헤더의 원본 데이터 */
    _item;
    /** 헤더의 value에 접두사를 붙인 데이터 */
    _prefixItem;
    /** 헤더의 부모 노드 */
    _parent;
    /** 헤더의 자식 노드들 */
    _childNodes;
    /** 고유 id */
    _id;
    /** 본인의 rowspan */
    _rowspan;
    /** 본인의 colspan */
    _colspan;
    /** 깊이(헤더 tr에서 몇 번째 tr인지 나타냅니다.) */
    _deep = -1;
    /** ThElement 객체 */
    _elementObject;

    constructor(item, prefixItem) {
        this._item = item;
        this._prefixItem = prefixItem;
        this._elementObject = new ThElement(this);
    }

    observe() {
        this._elementObject.findElementByTableId();
        this._elementObject.observe();
    }

    get elementObject() {
        return this._elementObject;
    }

    set deep(deep) {
        this._deep = deep;
    }

    get deep() {
        return this._deep;
    }

    get raw() {
        return this._item;
    }

    get prefixRaw() {
        return this._prefixItem;
    }

    get id() {
        return this._id;
    }

    get parent() {
        return this._parent;
    }

    get childNodes() {
        return this._childNodes;
    }

    get hasChildren() {
        return !!this._childNodes && this._childNodes.length > 0;
    }

    get rowspan() {
        return this._rowspan;
    }

    get colspan() {
        return this._colspan;
    }

    /**
     * 자식 노드를 추가합니다.
     *
     * @param childNode 추가할 자식 노드
     */
    pushChildNode(childNode) {
        if (this._childNodes === undefined) this._childNodes = [];
        this._childNodes.push(childNode);
    }

    /**
     * 루트 노드인지 검사합니다
     *
     * @returns {boolean}
     */
    get isRootNode() {
        return !this._parent;
    }

    get hasChildNodes() {
        return this._childNodes !== null && this._childNodes !== undefined && Array.isArray(this._childNodes) && this._childNodes.length > 0;
    }
}

class TableHeaderManager {
    // 루트 노트 배열
    _root = [];
    // 트리 구조의 노드를 직렬화(1차원 배열화)한 배열
    _nodes = [];
    // 테이블 헤더의 maxRowspan 입니다.
    _maxRowspan = 0;
    // 테이블 구조에 따라 colspan 의 값을 조정합니다.
    _conditioningColspan = 0;
    // 헤더 value의 prefix를 설정합니다.
    _prefix;

    constructor(headers) {
        // 생성자에서 헤더배열이 주어지면 헤더 트리 노드 생성 시작!
        if (headers) this.headers = headers;
    }

    /**
     * vue-data-table 의 헤더를 저장합니다.
     *
     * 들어온 트리구조의 헤더 배열은 노드화 되며
     *
     * 테이블에서 필요로 하는 값들을 계산하여 내부적으로 저장합니다.
     *
     * @param headers vue-data-table 헤더 배열
     *
     * 주의!
     *  - 각각의 기능단위들의 실행 순서를 변경하지 마세요!
     *      순서가 달라지면 버그가 발생합니다!
     */
    setHeaders(headers, prefix) {
        const prefixHeaders = [];
        cloneDeep(headers, prefixHeaders);

        // 새로운 헤더 노드 트리를 위해 내부 정보를 모두 초기화합니다.
        this.truncate();
        // 트리 구조의 헤더를 노드화 시킵니다.
        this._root = this.treenize(headers, prefixHeaders);
        // 트리 구조의 노드를 직렬화 합니다.
        this._nodes = this.serialize();
        // 각각의 헤더 value에 접두사를 붙입니다.
        this._nodes.forEach(e => {
            switch(e.raw.value) {
                case 'data-table-expand':
                case 'data-table-select':
                    break;
                default:
                    e.prefixRaw.value = prefix + '.' + e.raw.value
                    break;
            }
        });
        // 트리화된 노드들의 부모를 찾아줍니다.
        this.matchParent();
        // 트리화된 각각의 노드들의 colspan 을 구합니다.
        this.matchColspan();
        // 테이블 헤더의 max rowspan 을 구합니다.
        this._maxRowspan = this.getMaxRowspan();
        // 트리화된 각각의 노드들의 rowspan 을 구합니다.
        this.matchRowspan();
        // 각 노드에 고유한 ID 를 부여합니다.
        this.unique();
    }

    /**
     * 초기화 작업을 합니다.
     */
    truncate() {
        this._root = [];
        this._nodes = [];
        this._maxRowspan = 0;
        this._conditioningColspan = 0;
    }

    /**
     * 각 헤더 노드에 해당하는 DOM Element 를 찾아 Observer 를 등록합니다.
     */
    observe() {
        this._nodes.forEach(node => {
            node.observe();
        });
    }

    get headerHeight() {
        if (!this._root || this._root.length < 1) return 0;
        let node = this._root[0]; // 아무 노드나 해도 됩니다. 테이블 헤더는 모든 컬럼이 동일합니다.
        let total = 0;

        while (!!node) {
            total += node.elementObject.height;
            if (!node.hasChildNodes) break; // 자식이 없다면 중단합니다.
            node = node.childNodes[0]; // 아무 자식 노드나 해도 됩니다.
        }

        return total;
    }

    /**
     * 루트 노드 배열을 반환합니다.
     */
    get root() {
        return this._root;
    }

    /**
     * 직렬화된 노드 배열을 반환합니다.
     */
    get nodes() {
        return this._nodes;
    }

    /**
     * 말단 노드의 배열을 반환합니다.
     */
    get endNodes() {
        return this._nodes.filter(e => !e.hasChildNodes /* 자식이 없으면 말단 노드입니다. */);
    }

    /**
     * 말단 헤더의 배열을 반환합니다.
     */
    get endHeaders() {
        return this.endNodes.map(e => e.prefixRaw);
    }

    /**
     * 테이블 헤더의 max rowspan 을 반환합니다.
     */
    get maxRowspan() {
        return this._maxRowspan;
    }

    /**
     * max colspan 을 반환합니다.
     */
    get maxColspan() {
        return this.endNodes.length + this._conditioningColspan;
    }

    /**
     * 일반 배열인 headers 를 노드화 합니다.
     *
     * 일반 배열인 headers 는 트리 구조를 가지고 있습니다. (자식이 존재함)
     *
     * 결과물인 node 의 구조또한 트리 구조입니다.
     */
    treenize(headers, prefixHeaders) {
        const treeNode = [];
        for (let i = 0; i < headers.length; i++) {
            const node = new ThNode(headers[i], prefixHeaders[i]);
            if (this.hasChild(node.raw)) { // 헤더의 자식이 있다면 자식들도 모두 트리 노드화 해줍니다.
                const childNodeArray = this.treenize(headers[i].child, prefixHeaders[i].child); // 재귀 호출입니다. 주의하세요!
                childNodeArray.forEach(childNode => { // 자식 노드로 등록합니다.
                    node.pushChildNode(childNode);
                });
            }
            treeNode.push(node);
        }
        return treeNode;
    }

    /**
     * 트리 구조의 노드를 직렬화합니다.
     */
    serialize(nodeArray = this._root) {
        const nodes = [];
        nodeArray.forEach(node => {
            nodes.push(node);
            if (node.hasChildNodes) { // 자식이 존재하면 재귀 호출하여 말단 노드까지 같은 작업을 반복합니다.
                const childNodes = this.serialize(node.childNodes); // 재귀 호출입니다. 주의하세요!
                nodes.push(...childNodes); // 결과값을 더합니다.
            }
        });
        return nodes;
    }

    /**
     * 트리 노드에서 각각의 노드가 부모를 찾습니다.
     */
    matchParent(nodeArray = this._root, parent = undefined) {
        nodeArray.forEach(node => {
            if (node.hasChildNodes) { // 자식이 존재하면 자식에게 부모를 알립니다.
                this.matchParent(node.childNodes, node); // 재귀 호출입니다. 주의하세요!
            }
            node._parent = parent;
        });
    }

    /**
     * max rowspan 을 반환합니다.
     */
    getMaxRowspan(nodeArray = this._root, deep = 1) {
        let maxRowspan = deep;
        nodeArray.forEach(node => {
            node._deep = deep; // deep 을 저장합니다.
            if (node.hasChildNodes) {
                // 자식이 있다면 자식의 깊이를 구해서 현재의 maxRowspan 보다 클 경우 현재의 maxRowspan 의 값을 자식의 깊이로 갱신합니다.
                const childDeep = this.getMaxRowspan(node.childNodes, deep + 1); // 재귀 호출입니다. 주의하세요!
                if (maxRowspan < childDeep) maxRowspan = childDeep;
            }
        });
        return maxRowspan;
    }

    /**
     * 각 테이블 헤더의 rowspan 을 구해서 노드에 저장합니다.
     *
     * 복잡한 구조에서 테이블 헤더의 rowspan 을 계산하는데 어려움이 있어 아래와 같은 규칙을 따릅니다.
     *
     * 규칙!
     *  - 단일 노드(부모, 자식 모두 없는 루트 노드) 이면 maxRowspan
     *  - 그 외 모두 1
     */
    matchRowspan(nodeArray = this._root) {
        nodeArray.forEach(node => {
            if (node.hasChildNodes) {
                this.matchRowspan(node.childNodes);
            }
            node._rowspan = node.isRootNode && !node.hasChildNodes ? this.maxRowspan : 1;
        });
    }

    /**
     * 각 테이블 헤더의 colspan 을 구해서 노드에 저장합니다.
     *
     * @param nodeArray
     * @returns {number} 본인의 colspan
     */
    matchColspan(nodeArray = this._root) {
        let colspan = 0;
        nodeArray.forEach(node => {
            if (node.hasChildNodes) { // 자식이 존재하면 자식들의 colspan 을 저장합니다.(바로 아래 자식들의 colspan 의 합입니다.)
                node._colspan = this.matchColspan(node.childNodes);
                colspan += node._colspan; // colspan 누적(반환값)
            } else { // 자식이 없습니다 이 노드의 colspan 은 1입니다.
                node._colspan = 1;
                colspan++; // colspan 누적(반환값)
            }
        });
        return colspan;
    }

    /**
     * 각 노드에 고유한 ID 를 부여합니다.
     */
    unique() {
        this._nodes.forEach(node => node._id = getUniqueId());
    }

    /**
     * max colspan 의 값을 조정합니다.
     */
    addColspan() {
        this._conditioningColspan++;
    }

    /**
     * max colspan 의 값을 조정합니다.
     */
    minusColspan() {
        this._conditioningColspan--;
    }

    /**
     * 일반 배열인 header 가 자식을 가지고 있는지 검사합니다.
     *
     * @param header vue-data-table 의 헤더
     * @returns {boolean} 자식이 있으면 true, 없으면 false
     */
    hasChild(header) {
        return header.child !== null && header.child !== undefined
            && Array.isArray(header.child) && header.child.length > 0;
    }
}

export {TableHeaderManager}
